import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";

export default function ClientInvoices() {
  const { profile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (profile?.client_id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  async function load() {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from("invoices")
      .select("*, projects(name)")
      .eq("client_id", profile.client_id)
      .order("invoice_date", { ascending: false });
    if (fetchError) setError(fetchError.message);
    setInvoices(data || []);
    setLoading(false);
  }

  const filtered = useMemo(() => {
    if (filter === "all") return invoices;
    return invoices.filter((i) => i.status === filter);
  }, [invoices, filter]);

  async function download(invoice) {
    if (!invoice.file_url) return;
    const { data, error: urlError } = await supabase.storage
      .from("invoices")
      .createSignedUrl(invoice.file_url, 60);
    if (urlError) {
      setError(urlError.message);
      return;
    }
    window.open(data.signedUrl, "_blank");
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Invoices</h1>
      <p className="text-sm text-muted-foreground mb-5">{invoices.length} invoices</p>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2 mb-4">{error}</p>
      )}

      <div className="flex gap-2 mb-4">
        {["all", "paid", "unpaid", "overdue"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`font-mono text-[11.5px] px-3 py-1.5 rounded-full border ${
              filter === f ? "bg-primary text-primary-foreground border-primary" : "border-input text-muted-foreground"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        {loading ? (
          <p className="text-sm text-muted-foreground p-5">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground p-5">No invoices here.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Invoice</th>
                <th className="text-left font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Project</th>
                <th className="text-left font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Date</th>
                <th className="text-right font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Amount</th>
                <th className="text-left font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} className="border-b border-border last:border-0">
                  <td className="px-5 py-3.5 font-medium text-foreground">{inv.invoice_number}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{inv.projects?.name || "-"}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{inv.invoice_date}</td>
                  <td className="px-5 py-3.5 text-right">₹{Number(inv.amount).toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`font-mono text-[10.5px] rounded-full px-2.5 py-1 ${
                        inv.status === "paid" ? "bg-accent text-foreground" : "bg-destructive/10 text-destructive"
                      }`}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    {inv.file_url && (
                      <button onClick={() => download(inv)} className="text-xs text-muted-foreground hover:text-foreground underline">
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
